<ng-template let-extraClass="extraClass" #topNav>
  @if (!windowService.isDesktopRes) {
    <tidy-top-nav
      [class]="extraClass"
      [title]="'My Account'"
      [canGoBack]="true"
      [customBack]="'more'" />
  }
</ng-template>

<ng-container *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<ng-template #content>
<tidy-wrapper>
  @if (isLoaded) {

    <!-- Account Manager: photo | role + name | call | text | schedule -->
    <tidy-alert class="black-background obs-am">
      <div class="obs-am-row">
        <img [src]="managerPhoto" class="obs-am-avatar" [alt]="managerName" />
        <div class="obs-am-identity">
          <span class="obs-am-eyebrow">Account Manager</span>
          <tidy-text [body]="managerName" class="obs-am-name" />
        </div>
        <div class="obs-am-actions">
          <tidy-small-button [text]="'Email'" [image]="'assets/svg/mail-outline.svg'" (buttonClick)="email()" />
          <tidy-small-button [text]="'Text'" [image]="'assets/svg/chatbox-ellipses-outline.svg'" (buttonClick)="text()" />
          <tidy-small-button [text]="'Schedule'" [image]="'assets/svg/calendar-outline.svg'" (buttonClick)="schedule()" />
        </div>
      </div>

      @if ((callScheduled && !setupCallCompleted) || targetGoLiveDateDisplay) {
        <tidy-divider />
        @if (callScheduled && !setupCallCompleted) {
          <div class="obs-am-scheduled">
            <tidy-image [src]="'assets/svg/call-outline.svg'" class="obs-am-scheduled-icon body-size" />
            <tidy-text [body]="('Setup call scheduled with ' | translate) + managerName" />
          </div>
        }
        @if (targetGoLiveDateDisplay) {
          <div class="obs-am-scheduled">
            <tidy-image [src]="'assets/svg/locate-outline.svg'" class="obs-am-scheduled-icon body-size" />
            <tidy-text [body]="('Go Live Date: ' | translate) + targetGoLiveDateDisplay" />
          </div>
        }
      }
    </tidy-alert>

    <!-- Account Settings: which rental businesses this account runs. Each toggle
         gates a whole feature set. -->
    <tidy-alert class="black-background obs-settings">
      <div class="obs-settings-head">
        <span class="obs-am-eyebrow">Account Settings</span>
      </div>

      <div class="obs-settings-row">
        <div class="obs-settings-main">
          <tidy-text [body]="'Long-term rentals'" class="obs-settings-label" />
          <tidy-text [helper]="'Leasing, applications, screenings, leases, and rent collection.'" class="obs-settings-sub" />
        </div>
        <tidy-toggle [toggled]="longTermRentals" (toggleChanged)="onLongTermRentalsChange()" />
      </div>

      <div class="obs-settings-row">
        <div class="obs-settings-main">
          <tidy-text [body]="'Short-term rentals'" class="obs-settings-label" />
          <tidy-text [helper]="'Nightly pricing, reservations, guests, and channel listings.'" class="obs-settings-sub" />
        </div>
        <tidy-toggle [toggled]="shortTermRentals" (toggleChanged)="onShortTermRentalsChange()" />
      </div>

      @if (!longTermRentals && !shortTermRentals) {
        <div class="obs-settings-note">
          <tidy-text [helper]="'Turn on at least one to see your rental features.'" />
        </div>
      }
    </tidy-alert>

    @if (hasAddress) {
      @for (address of addresses; track address.addressId) {
        <div class="obs-group">
          <div class="obs-group-header">
            <span class="obs-status-dot" [class.live]="address.isLive"></span>
            <tidy-text [body]="address.name" class="obs-group-name" />
            <span class="obs-chip" [class.obs-chip-live]="address.isLive" [class.obs-chip-pending]="!address.isLive">
              {{ address.isLive ? 'Live' : 'Setup needed' }}
            </span>
          </div>

          @for (item of address.items; track item.stepId) {
            @if (!item.done) {
              <button class="obs-step-card" (click)="startItem(address, item)">
                <span class="obs-radio"></span>
                <span class="obs-step-text">
                  <tidy-text [body]="item.title" />
                </span>
                <span class="obs-item-chevron material-icons">chevron_right</span>
              </button>
            }
          }

          @if (showConnectListingsFor(address)) {
            <button class="obs-step-card" (click)="openListingsSetup(address.addressId)">
              <span class="obs-radio"></span>
              <span class="obs-step-text">
                <tidy-text [body]="'Tell Us About Your Listings'" />
              </span>
              <span class="obs-item-chevron material-icons">chevron_right</span>
            </button>
          }

          @if (showSubscribeFor(address)) {
            <button class="obs-step-card" (click)="startSubscribe(address)">
              <span class="obs-radio"></span>
              <span class="obs-step-text">
                <tidy-text [body]="'Subscribe'" />
              </span>
              <span class="obs-item-chevron material-icons">chevron_right</span>
            </button>
          }

          @if (hasCompletedItems(address)) {
            <tidy-row class="obs-completed-toggle">
              <tidy-text
                [actionBody]="isCompletedExpanded(address) ? 'Hide Completed Items' : 'View Completed Items'"
                class="white-link"
                (action)="toggleCompleted(address)" />
            </tidy-row>

            @if (isCompletedExpanded(address)) {
              @for (item of address.items; track item.stepId) {
                @if (item.done) {
                  <div class="obs-step-card obs-step-card--done">
                    <span class="obs-radio obs-radio--filled">
                      <span class="material-icons">check</span>
                    </span>
                    <span class="obs-step-text">
                      <tidy-text [body]="item.title" />
                    </span>
                  </div>
                }
              }
            }
          }
        </div>
      }
    } @else {
      <!-- No property yet (regardless of goal): "Your First Property" placeholder + the
           Tell Us About Your Listings item (the flow asks for a property first) and Subscribe
           (account-wide, so it needs no property). -->
      <div class="obs-group">
        <div class="obs-group-header">
          <span class="obs-status-dot"></span>
          <tidy-text [body]="'Your First Property'" class="obs-group-name" />
          <span class="obs-chip obs-chip-pending">Setup needed</span>
        </div>
        <button class="obs-step-card" (click)="openListingsSetup()">
          <span class="obs-radio"></span>
          <span class="obs-step-text">
            <tidy-text [body]="'Tell Us About Your Listings'" />
          </span>
          <span class="obs-item-chevron material-icons">chevron_right</span>
        </button>
        @if (!subscribeCompleted) {
          <button class="obs-step-card" (click)="startSubscribe()">
            <span class="obs-radio"></span>
            <span class="obs-step-text">
              <tidy-text [body]="'Subscribe'" />
            </span>
            <span class="obs-item-chevron material-icons">chevron_right</span>
          </button>
        }
      </div>
    }

    @if (showDetermineNote) {
      <tidy-row class="obs-note" [align]="'center'">
        <tidy-text [helper]="'Your account manager will determine any additional action items on your call.'" />
      </tidy-row>
    }

  }
</tidy-wrapper>
</ng-template>

@if (!windowService.isDesktopRes) {
  <app-scroll-wrapper [templateContent]="content" />
} @else {
  <ng-container *ngTemplateOutlet="content" />
}
